<template>
  <div class="storage-tables-page">
    <div class="storage-tables-list">
      <h1>App Deploy Table Viewer</h1>
      <p class="description">
        Select a table to view existing App Deployment Lists or Create and
        Update App Lists.
      </p>
      <div class="button-container">
        <button
          v-if="!selectedTable"
          @click="fetchTables"
          class="primary-button"
        >
          Refresh Tables
        </button>
        <button
          v-if="selectedTable"
          @click="showTableList"
          class="secondary-button"
        >
          Back to Table List
        </button>
        <router-link
          v-if="!selectedTable"
          to="/create-azure-storage-table"
          class="primary-button"
        >
          Create New Client Table
        </router-link>
        <router-link
          v-if="!selectedTable"
          to="/update-existing-table"
          class="primary-button"
        >
          Update Existing Table
        </router-link>
        <router-link
          v-if="!selectedTable"
          to="/AppDeploy-Onboarding"
          class="primary-button"
        >
          Onboard App Deploy Client
        </router-link>
        <router-link
          v-if="!selectedTable"
          to="/AppDeploy-Onboarding-New"
          class="primary-button new-flow"
        >
          Onboard App Deploy Client (New)
        </router-link>
      </div>

      <!-- Add the Table Type Filter -->
      <div v-if="!selectedTable" class="filter-section">
        <label for="tableTypeFilter">Filter by type:</label>
        <select v-model="selectedTableType" id="tableTypeFilter">
          <option value="">All Tables</option>
          <option value="WorkstationApps">Workstation Apps</option>
          <option value="RemotePCs">Remote PCs</option>
          <option value="ServerApps">Server Apps</option>
        </select>
      </div>

      <!-- Add the Search Bar -->
      <TableListSearchBar
        v-if="!selectedTable"
        v-model="searchQuery"
        placeholder="Search tables..."
      />

      <!-- Display Filtered Tables -->
      <ul v-if="!selectedTable" class="table-list">
        <li
          v-for="table in filteredTables"
          :key="table.name"
          @click="fetchTableContents(table.name)"
          class="table-item"
        >
          <span class="table-name">{{ table.name }}</span>
          <span class="table-type-badge">{{ getTableType(table.name) }}</span>
        </li>
      </ul>

      <div v-if="selectedTable && tableContents.length" class="table-wrapper">
        <h2>{{ selectedTable }} Applications</h2>

        <!-- Message display section -->
        <div v-if="message.text" :class="['message', message.type]">
          {{ message.text }}
        </div>

        <div class="table-scroll-container">
          <div class="table-scrollbar" ref="topScroll">
            <div class="table-scroll-content"></div>
          </div>
          <div class="table-container" ref="tableContainer">
            <table>
              <thead>
                <tr>
                  <th
                    v-for="header in tableHeaders"
                    :key="header"
                    @click="sortTable(header)"
                  >
                    {{ header }}
                    <span v-if="sortColumn === header">
                      {{ sortOrder === "asc" ? "▲" : "▼" }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in sortedTableContents"
                  :key="row.PartitionKey + '-' + row.rowKey"
                >
                  <td :class="statusColor(row.Status)" class="table-cell">
                    {{ row.Status }}
                  </td>
                  <td
                    v-for="header in filteredHeaders"
                    :key="header"
                    class="table-cell"
                  >
                    {{ row[header] }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-scrollbar" ref="bottomScroll">
            <div class="table-scroll-content"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TableListSearchBar from "@/views/components/TableListSearchBar.vue";
import { msalInstance } from "@/auth.js";
import { listAzureTables, getTableContents } from "@/azure-devops-api.js";
import { mapState } from "vuex";

export default {
  components: {
    TableListSearchBar,
  },
  data() {
    return {
      tables: [],
      searchQuery: "",
      selectedTable: null,
      selectedTableType: "",
      tableContents: [],
      tableHeaders: [],
      sortColumn: "",
      sortOrder: "asc",
      message: {
        text: "",
        type: "",
      },
    };
  },
  computed: {
    ...mapState(["isLoggedIn"]),
    filteredTables() {
      const query = this.searchQuery.trim().toLowerCase();
      console.log("Filtering tables with query:", query);

      let filtered = this.tables;

      // Filter by search query
      if (query) {
        filtered = filtered.filter((table) => {
          const tableName = table.name ? table.name.toLowerCase() : "";
          return tableName.startsWith(query);
        });
      }

      // Filter by table type
      if (this.selectedTableType) {
        filtered = filtered.filter((table) => {
          return table.name.endsWith(this.selectedTableType);
        });
      }

      console.log("Filtered Tables:", filtered);
      return filtered;
    },
    sortedTableContents() {
      if (!this.sortColumn) return this.tableContents;
      return this.tableContents.slice().sort((a, b) => {
        const aValue = a[this.sortColumn];
        const bValue = b[this.sortColumn];
        return this.sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    },
    filteredHeaders() {
      return this.tableHeaders.filter((header) => header !== "Status");
    },
  },
  async created() {
    try {
      console.log("Fetching tables...");
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length) {
        const accessTokenRequest = {
          account: accounts[0],
          scopes: ["https://storage.azure.com/.default"],
        };
        await msalInstance.acquireTokenSilent(accessTokenRequest);
        this.tables = await listAzureTables();
        console.log("Tables loaded:", this.tables);
      }
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  },
  methods: {
    async fetchTables() {
      try {
        console.log("Refreshing tables...");
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) throw new Error("No accounts found");
        const accessTokenRequest = {
          account: accounts[0],
          scopes: ["https://storage.azure.com/.default"],
        };
        await msalInstance.acquireTokenSilent(accessTokenRequest);
        this.tables = await listAzureTables();
        console.log("Refreshed tables:", this.tables);
      } catch (error) {
        console.error("Error refreshing tables:", error);
      }
    },
    async fetchTableContents(tableName) {
      try {
        console.log("Loading contents for:", tableName);
        this.selectedTable = tableName;
        this.tableContents = await getTableContents(tableName);
        this.tableHeaders = Object.keys(this.tableContents[0] || {});
        console.log("Loaded table contents:", this.tableContents);
      } catch (error) {
        console.error("Error loading table contents:", error);
      }
    },
    showTableList() {
      this.selectedTable = null;
    },
    sortTable(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortOrder = "asc";
      }
    },
    statusColor(status) {
      return {
        "status-enabled": status === "Enabled",
        "status-disabled": status === "Disabled",
      };
    },
    getTableType(tableName) {
      if (tableName.endsWith("WorkstationApps")) return "Workstation Apps";
      if (tableName.endsWith("RemotePCs")) return "Remote PCs";
      if (tableName.endsWith("ServerApps")) return "Server Apps";
      return "Unknown";
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: "Roboto", sans-serif;
  line-height: 1.6;
}

.container {
  width: 100%;
  margin: 0 auto;
}

.header {
  background: #0e223b;
  color: #fff;
}

.top-bar {
  background: #ffffff;
  padding: 20px 0;
  text-align: center;
}

.top-bar .logo {
  text-align: left;
}

.top-bar .logo img {
  width: 150px;
  margin-left: 20px;
}

.nav-bar {
  background: #0e223b;
  padding: 10px 0;
}

.description {
  font-size: 1rem;
  color: #555;
  margin-bottom: 20px;
}

.nav-bar a {
  color: #fff;
  margin: 0 15px;
  text-decoration: none;
  transition: color 0.3s;
}

.nav-bar a:hover {
  color: #38b35f;
}

.storage-tables-list {
  max-width: 1650px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
  font-family: "Roboto", sans-serif;
}

h1 {
  font-size: 2rem;
  margin-bottom: 20px;
  font-family: "Roboto", sans-serif;
}

.button-container {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.primary-button,
.secondary-button {
  font-family: "Roboto", sans-serif;
  padding: 10px 20px;
  font-size: 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.primary-button {
  background-color: #38b35f;
  color: white;
}

.primary-button:hover {
  background-color: #1c7ed6;
}

.primary-button.new-flow {
  background-color: #1e90ff;
  position: relative;
}

.primary-button.new-flow:hover {
  background-color: #1c7ed6;
}

.primary-button.new-flow::after {
  content: "NEW";
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ff4757;
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 10px;
  line-height: 1;
}

.secondary-button {
  background-color: #555;
  color: white;
}

.secondary-button:hover {
  background-color: #444;
}

.filter-section {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.filter-section label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
}

.filter-section select {
  padding: 8px 12px;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  font-family: "Roboto", sans-serif;
  transition: border-color 0.3s ease;
}

.filter-section select:hover {
  border-color: #38b35f;
}

.filter-section select:focus {
  outline: none;
  border-color: #38b35f;
  box-shadow: 0 0 0 2px rgba(56, 179, 95, 0.1);
}

ul.table-list {
  list-style-type: none;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

li.table-item {
  background-color: #0e223b;
  font-family: "Roboto", sans-serif;
  color: white;
  font-size: 1rem;
  padding: 15px 20px;
  border-radius: 4px;
  margin: 10px;
  width: 100%;
  max-width: 300px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

li.table-item:hover {
  background-color: #1c7ed6;
}

.table-name {
  font-weight: 500;
}

.table-type-badge {
  display: inline-block;
  padding: 4px 8px;
  font-size: 0.75rem;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-weight: 400;
}

.table-wrapper {
  width: 100%;
  overflow: hidden;
}

.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-scrollbar {
  overflow-x: auto;
  overflow-y: hidden;
  height: 20px;
}

.table-scroll-content {
  height: 1px;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #ddd;
}

table {
  width: 100%;
  margin: 20px 0;
  border-collapse: collapse;
  min-width: 600px;
}

th,
td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

th {
  background-color: #f4f4f4;
  cursor: pointer;
}

th span {
  font-size: 0.8rem;
}

tr:nth-child(even) {
  background-color: #f9f9f9;
}

tr:hover {
  background-color: #f1f1f1;
}

.table-cell {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message {
  margin: 20px 0;
  padding: 10px;
  border-radius: 4px;
  font-size: 1rem;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
}

.status-enabled {
  background-color: lightgreen;
}

.status-disabled {
  background-color: lightcoral;
}

td.table-cell.status {
  max-width: 100px;
  font-weight: bold;
  text-align: center;
}
</style>